import { useEffect, useRef, useState } from "react";
import { FaChevronLeft } from "react-icons/fa";
import { MdOutlineInfo } from "react-icons/md";
import MeritoButton from "../../MeritoButtons/MeritoButtons";
import secureLockImage from "../../../../resources/images/gst-recon/tally-secure-lock.png";
import sharedStyles from "../TallySyncShared.module.scss";
import styles from "./TallySyncReceiving.module.scss";

const receivingProgressTarget = 90;
const receivingProgressDurationMs = 90_000;

interface TallySyncReceivingProps {
  isDashboard?: boolean;
  receivingStartedAt?: number | null;
}

export default function TallySyncReceiving({
  isDashboard = false,
  receivingStartedAt = null,
}: TallySyncReceivingProps) {
  const startedAtRef = useRef(receivingStartedAt ?? Date.now());
  const elapsedAtRender = Math.max(0, Date.now() - startedAtRef.current);
  const [progress, setProgress] = useState(() =>
    Math.min(
      receivingProgressTarget,
      (elapsedAtRender / receivingProgressDurationMs) *
        receivingProgressTarget
    )
  );

  useEffect(() => {
    startedAtRef.current = receivingStartedAt ?? Date.now();
    const elapsed = Math.max(0, Date.now() - startedAtRef.current);
    setProgress(
      Math.min(
        receivingProgressTarget,
        (elapsed / receivingProgressDurationMs) * receivingProgressTarget
      )
    );

    let secondAnimationFrame = 0;
    const firstAnimationFrame = requestAnimationFrame(() => {
      secondAnimationFrame = requestAnimationFrame(() => {
        setProgress(receivingProgressTarget);
      });
    });

    return () => {
      cancelAnimationFrame(firstAnimationFrame);
      cancelAnimationFrame(secondAnimationFrame);
    };
  }, [receivingStartedAt]);

  const remainingDurationMs = Math.max(
    0,
    receivingProgressDurationMs -
      Math.max(0, Date.now() - startedAtRef.current)
  );

  const progressContent = (
    <div className={styles.receivingProgress}>
      <div
        className={`${styles.progressBarGradient} ${styles.receivingProgressBar}`}
        role="progressbar"
        aria-label="Receiving data from Tally"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
      >
        <div
          className={styles.progressFillGradient}
          style={{
            width: `${progress}%`,
            transition: `width ${remainingDurationMs}ms linear`,
          }}
        />
      </div>

      <div className={styles.secureTransferLabel}>
        <span className={styles.secureLock} aria-hidden="true">
          <img src={secureLockImage} alt="" />
        </span>
        <span>Sent directly and securely from Tally.</span>
      </div>
    </div>
  );

  if (isDashboard) {
    return (
      <div className={styles.dashboardContent}>
        <div className={styles.dashboardBody}>
          <div className={styles.dashboardHeader}>
            <h2>Syncing from Tally</h2>
            <p>Large datasets may take a few minutes. Keep Tally open.</p>
          </div>
          {progressContent}
        </div>
      </div>
    );
  }

  return (
    <div className={sharedStyles.page}>
      <div className={`${sharedStyles.shell} ${sharedStyles.compactShell}`}>
        <MeritoButton
          type="secondary-1"
          size="md2"
          className={`${sharedStyles.backBtn} ${sharedStyles.disabledBackBtn}`}
          disabled
          icon={
            <span className={sharedStyles.backIcon} aria-hidden="true">
              <FaChevronLeft />
            </span>
          }
          btnText="Back"
        />

        <div className={`${sharedStyles.card} ${sharedStyles.compactCard}`}>
          <div className={`${sharedStyles.content} ${styles.receivingContent}`}>
            <div className={sharedStyles.header}>
              <p className={sharedStyles.stepLabel}>
                STEP 2 OF 2 • Connect Automatically
              </p>
              <h1 className={sharedStyles.heading}>Syncing from Tally</h1>
            </div>

            <div className={styles.receivingBody}>
              <div className={styles.receivingNotice}>
                <span className={styles.receivingNoticeIcon} aria-hidden="true">
                  <MdOutlineInfo />
                </span>
                <p>
                  Sit tight, this may take a few minutes for larger datasets.
                  Please don&apos;t close Tally while it finishes.
                </p>
              </div>

              {progressContent}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
